import { useDataTypesHooks } from "../../../../../features/data-types"
import { RoutePath, useRoutesHooks } from "../../../../../features/routes"
import { DetailFieldProps, DetailPanel, FieldType, UIPermission, useGetCrumbs, Action, ModalId, useUIActions, Tab } from "../../../../../features/ui"
import { TableName } from "../../../../../tables"
import { CompanyUserName, useSystemStateHooks } from "../../../../system"
import { CustomerType } from "../../../state/types"
import { ProductYieldsTable } from "../product-yields-table/productYieldsTable"
import { ExcludedProductsTable } from "../excluded-products-table/excludedProductsTable"
import { EditCustomerTypeModal } from "../edit-customer-type-modal/editCustomerTypeModal"
import { DownloadPriceListModal } from "../download-price-list-modal/downloadPriceListModal"
import { isSameYield } from "../customerTypeUtils"

export const CustomerTypeDetail = () => {
  const { dataType } = useRoutesHooks().parseUrl()
  const customerType = dataType! as CustomerType
  const { productYields, ...data } = customerType

  const { userHasPermission } = useSystemStateHooks()
  const { openModal, toggleModal } = useUIActions()
  const readOnly = !userHasPermission(UIPermission.SAVE_CUSTOMER_TYPES) || customerType.archived
  const { formatNumber, formatDateTime } = useDataTypesHooks()

  const crumbs = useGetCrumbs(
    'Listas de Precios',
    RoutePath.CUSTOMER_TYPES,
    customerType.name
  )

  const actions: Action[] = [
    {
      icon: 'download',
      label: 'Descargar',
      kind: 'tertiary',
      onClick: () => openModal(ModalId.DOWNLOAD_CUSTOMER_TYPE_PDF)
    }
  ]

  if (!readOnly) {
    actions.push({
      icon: 'pencil-fill',
      label: 'Editar',
      kind: 'primary',
      onClick: () => openModal(ModalId.EDIT_CUSTOMER_TYPE)
    })
    actions.push({
      icon: 'trash-fill',
      label: 'Eliminar',
      kind: 'secondary',
      onClick: () => toggleModal(ModalId.REMOVE)
    })
  }

  const fields: DetailFieldProps[] = [
    {
      name: 'name',
      type: FieldType.TEXT,
      label: 'Nombre'
    },
    {
      name: 'yield',
      type: FieldType.PERCENTAGE,
      label: customerType.yield <= 0 ? 'Descuento' : 'Recargo',
      render: () => <>{formatNumber(Math.abs(customerType.yield))}%</>
    }
  ]
  // Los ajustes iguales al general no son particulares
  const activeYields = productYields.filter(py => !py.isExcluded && !isSameYield(py.yield, customerType.yield))
  const excludedYields = productYields.filter(py => py.isExcluded)
  if (activeYields.length > 0) {
    fields.push({
      name: 'productYields',
      type: FieldType.TABLE,
      table: TableName.PRODUCTS,
      render: () => (
        <ProductYieldsTable
          items={activeYields}
          baseCustomerTypeId={customerType.baseCustomerTypeId}
          readOnly
        />
      )
    })
  }
  const tabs: Tab[] = excludedYields.length > 0 ? [
    {
      id: 'excludedProducts',
      title: 'Productos Excluidos',
      content: (
        <ExcludedProductsTable
          items={excludedYields}
          readOnly
        />
      )
    }
  ] : []
  if (customerType.negativeStateUserId) {
    fields.push({
      name: 'negativeStateUserId',
      type: FieldType.TEXT,
      label: 'Archivado por',
      render: () => {
        const dateStr = customerType.negativeStateAt ? ` - ${formatDateTime(new Date(customerType.negativeStateAt))}` : ''
        return <><CompanyUserName userId={customerType.negativeStateUserId!} />{dateStr}</>
      }
    })
  }

  return (
    <>  
      <DetailPanel
        tableName={TableName.CUSTOMER_TYPES}
        crumbs={crumbs}
        fields={fields}
        data={data}
        icon='file-ruled'
        tabs={tabs}
        readOnly={readOnly}
        actions={actions}
      />
      <EditCustomerTypeModal customerType={customerType} />
      <DownloadPriceListModal customerType={customerType} />
    </>
  )
}
